import type { Dispatch, SetStateAction } from "react";
import {
  sanitizeAccountPatch,
  sanitizeBudgetPatch,
  sanitizeCalendarEventPatch,
  sanitizeDailyPlan,
  sanitizeDailyReview,
  sanitizeGoalPatch,
  sanitizeHabitPatch,
  sanitizeNewAccount,
  sanitizeNewCalendarEvent,
  sanitizeNewBudget,
  sanitizeNewGoal,
  sanitizeNewHabit,
  sanitizeNewInboxItem,
  sanitizeNewNote,
  sanitizeNewProject,
  sanitizeNewRecurringRule,
  sanitizeNewTask,
  sanitizeNewTransaction,
  sanitizeNotePatch,
  sanitizeProjectPatch,
  sanitizeRecurringRulePatch,
  sanitizeTaskPatch,
  sanitizeTransactionPatch,
  sanitizeWeeklyReview,
} from "../lib/domainItems";
import {
  markDirty,
  prepareReplacementState,
  removeCollectionItem,
  stamp,
} from "../lib/lifeOsMutations";
import { DEFAULT_ACCOUNT_ID, makeId } from "../lib/storage";
import { applyBatchToState } from "../lib/ai/assistant-batch-create";
import type { BatchItem } from "../lib/ai/assistant-batch-create";
import { findOrphanedFinanceRefs } from "../bento/finance-orphan-derive";
import { buildProjectFromTemplate, captureOffsetDays } from "../bento/project-template-derive";
import { sweepRecurringTasks } from "../bento/recurring-task-derive";
import {
  buildSeedFinanceCategories,
  CATEGORY_COLOR_PALETTE,
  financeCategoryId,
  STANDARD_FINANCE_CATEGORIES,
} from "../bento/finance-categories";
import { todayIso } from "../lib/date";
import { deleteAttachmentObjects } from "../lib/attachment-storage";
import type {
  Account,
  Budget,
  CalendarEvent,
  CurrencyRate,
  DailyPlan,
  DailyReview,
  FinanceCategory,
  Goal,
  Habit,
  InboxConversionTarget,
  InboxItem,
  LifeOsCollectionName,
  LifeOsState,
  Note,
  Project,
  ProjectTemplate,
  RecurringRule,
  Task,
  Transaction,
  TransactionType,
  WeeklyReview,
} from "../types";

type Mutate = (recipe: (current: LifeOsState) => LifeOsState) => void;
type SetState = Dispatch<SetStateAction<LifeOsState>>;

export function buildLifeOsActions(mutate: Mutate, setState: SetState) {
  return {
    addTask(task: Omit<Task, "id">) {
      const id = makeId("task");
      const sanitizedTask = sanitizeNewTask(task);
      mutate((current) => {
        const next = markDirty(current, "tasks", id);
        return { ...next, tasks: [{ ...sanitizedTask, id }, ...current.tasks] };
      });
    },
    addEntitiesBatch(items: BatchItem[]) {
      // Functional mutate → ids/links compose against the freshest state and only
      // the new items are marked dirty (vs replaceState which re-pushes everything).
      mutate((current) => applyBatchToState(current, items));
    },
    updateTask(id: string, patch: Partial<Task>) {
      mutate((current) => {
        if (!current.tasks.some((task) => task.id === id)) {
          return current;
        }
        const next = markDirty(current, "tasks", id);
        return {
          ...next,
          tasks: current.tasks.map((task) => (task.id === id ? { ...task, ...sanitizeTaskPatch(patch, task) } : task)),
        };
      });
    },
    addProject(project: Omit<Project, "id">) {
      const id = makeId("project");
      const sanitizedProject = sanitizeNewProject(project);
      mutate((current) => {
        const next = markDirty(current, "projects", id);
        return { ...next, projects: [{ ...sanitizedProject, id }, ...current.projects] };
      });
    },
    updateProject(id: string, patch: Partial<Project>) {
      mutate((current) => {
        if (!current.projects.some((project) => project.id === id)) {
          return current;
        }
        const next = markDirty(current, "projects", id);
        return {
          ...next,
          projects: current.projects.map((project) =>
            project.id === id ? { ...project, ...sanitizeProjectPatch(patch, project) } : project,
          ),
        };
      });
    },
    addGoal(goal: Omit<Goal, "id">) {
      const id = makeId("goal");
      const sanitizedGoal = sanitizeNewGoal(goal);
      mutate((current) => {
        const next = markDirty(current, "goals", id);
        return { ...next, goals: [{ ...sanitizedGoal, id }, ...current.goals] };
      });
    },
    updateGoal(id: string, patch: Partial<Goal>) {
      mutate((current) => {
        if (!current.goals.some((goal) => goal.id === id)) {
          return current;
        }
        const next = markDirty(current, "goals", id);
        return {
          ...next,
          goals: current.goals.map((goal) => (goal.id === id ? { ...goal, ...sanitizeGoalPatch(patch, goal) } : goal)),
        };
      });
    },
    addHabit(habit: Omit<Habit, "id" | "completions">) {
      const id = makeId("habit");
      const sanitizedHabit = sanitizeNewHabit(habit);
      mutate((current) => {
        const next = markDirty(current, "habits", id);
        return { ...next, habits: [{ ...sanitizedHabit, id, completions: [] }, ...current.habits] };
      });
    },
    updateHabit(id: string, patch: Partial<Habit>) {
      mutate((current) => {
        if (!current.habits.some((habit) => habit.id === id)) {
          return current;
        }
        const next = markDirty(current, "habits", id);
        return {
          ...next,
          habits: current.habits.map((habit) =>
            habit.id === id ? { ...habit, ...sanitizeHabitPatch(patch, habit) } : habit,
          ),
        };
      });
    },
    toggleHabit(id: string, date: string) {
      mutate((current) => {
        if (!current.habits.some((habit) => habit.id === id)) {
          return current;
        }
        const next = markDirty(current, "habits", id);
        return {
          ...next,
          habits: current.habits.map((habit) => {
          if (habit.id !== id) {
            return habit;
          }
          const done = habit.completions.includes(date);
          return {
            ...habit,
            completions: done
              ? habit.completions.filter((completion) => completion !== date)
              : [...habit.completions, date],
          };
          }),
        };
      });
    },
    addNote(note: Omit<Note, "id" | "updatedAt">) {
      const id = makeId("note");
      const sanitizedNote = sanitizeNewNote(note);
      mutate((current) => {
        const next = markDirty(current, "notes", id);
        return { ...next, notes: [{ ...sanitizedNote, id, updatedAt: new Date().toISOString() }, ...current.notes] };
      });
    },
    updateNote(id: string, patch: Partial<Note>) {
      mutate((current) => {
        if (!current.notes.some((note) => note.id === id)) {
          return current;
        }
        const next = markDirty(current, "notes", id);
        return {
          ...next,
          notes: current.notes.map((note) =>
            note.id === id ? { ...note, ...sanitizeNotePatch(patch, note), updatedAt: new Date().toISOString() } : note,
          ),
        };
      });
    },
    addTransaction(transaction: Omit<Transaction, "id">) {
      const id = makeId("txn");
      const sanitizedTransaction = sanitizeNewTransaction(transaction);
      mutate((current) => {
        const next = markDirty(current, "transactions", id);
        return { ...next, transactions: [{ ...sanitizedTransaction, id }, ...current.transactions] };
      });
    },
    updateTransaction(id: string, patch: Partial<Transaction>) {
      mutate((current) => {
        if (!current.transactions.some((transaction) => transaction.id === id)) {
          return current;
        }
        const next = markDirty(current, "transactions", id);
        return {
          ...next,
          transactions: current.transactions.map((transaction) =>
            transaction.id === id ? { ...transaction, ...sanitizeTransactionPatch(patch, transaction) } : transaction,
          ),
        };
      });
    },
    addBudget(budget: Omit<Budget, "id">) {
      const id = makeId("budget");
      const sanitizedBudget = sanitizeNewBudget(budget);
      mutate((current) => {
        const next = markDirty(current, "budgets", id);
        return { ...next, budgets: [{ ...sanitizedBudget, id }, ...current.budgets] };
      });
    },
    updateBudget(id: string, patch: Partial<Budget>) {
      mutate((current) => {
        if (!current.budgets.some((budget) => budget.id === id)) {
          return current;
        }
        const next = markDirty(current, "budgets", id);
        return {
          ...next,
          budgets: current.budgets.map((budget) =>
            budget.id === id ? { ...budget, ...sanitizeBudgetPatch(patch, budget) } : budget,
          ),
        };
      });
    },
    addInboxItem(item: Omit<InboxItem, "id" | "createdAt"> & Partial<Pick<InboxItem, "createdAt">>) {
      const id = makeId("inbox");
      const sanitizedItem = sanitizeNewInboxItem({
        ...item,
        createdAt: item.createdAt ?? new Date().toISOString(),
      });
      mutate((current) => {
        const next = markDirty(current, "inbox", id);
        return { ...next, inbox: [{ ...sanitizedItem, id }, ...current.inbox] };
        // Return the id so callers can target THIS item (clarify modal) instead of
        // guessing "newest in list" — which breaks under realtime/multi-tab updates.
      });
      return id;
    },
    convertInboxItem(id: string, target: InboxConversionTarget) {
      const targetId = makeId(target.type);
      mutate((current) => {
        if (!current.inbox.some((item) => item.id === id)) {
          return current;
        }

        let next: LifeOsState = current;
        if (target.type === "task") {
          next = markDirty(next, "tasks", targetId);
          next = { ...next, tasks: [{ ...sanitizeNewTask(target.item), id: targetId }, ...next.tasks] };
        }
        if (target.type === "project") {
          next = markDirty(next, "projects", targetId);
          next = { ...next, projects: [{ ...sanitizeNewProject(target.item), id: targetId }, ...next.projects] };
        }
        if (target.type === "goal") {
          next = markDirty(next, "goals", targetId);
          next = { ...next, goals: [{ ...sanitizeNewGoal(target.item), id: targetId }, ...next.goals] };
        }
        if (target.type === "note") {
          next = markDirty(next, "notes", targetId);
          next = {
            ...next,
            notes: [{ ...sanitizeNewNote(target.item), id: targetId, updatedAt: new Date().toISOString() }, ...next.notes],
          };
        }
        if (target.type === "habit") {
          next = markDirty(next, "habits", targetId);
          next = { ...next, habits: [{ ...sanitizeNewHabit(target.item), id: targetId, completions: [] }, ...next.habits] };
        }
        if (target.type === "event") {
          next = markDirty(next, "events", targetId);
          next = { ...next, events: [{ ...sanitizeNewCalendarEvent(target.item), id: targetId }, ...next.events] };
        }

        return removeCollectionItem(next, "inbox", id);
      });
    },
    updateInboxItem(id: string, patch: Partial<InboxItem>) {
      mutate((current) => {
        if (!current.inbox.some((item) => item.id === id)) {
          return current;
        }
        const next = markDirty(current, "inbox", id);
        return {
          ...next,
          inbox: current.inbox.map((item) => (item.id === id ? { ...item, ...patch } : item)),
        };
      });
    },
    removeInboxItem(id: string) {
      mutate((current) => removeCollectionItem(current, "inbox", id));
    },
    addEvent(event: Omit<CalendarEvent, "id">) {
      const id = makeId("event");
      const sanitizedEvent = sanitizeNewCalendarEvent(event);
      mutate((current) => {
        const next = markDirty(current, "events", id);
        return { ...next, events: [{ ...sanitizedEvent, id }, ...current.events] };
      });
    },
    updateEvent(id: string, patch: Partial<CalendarEvent>) {
      mutate((current) => {
        if (!current.events.some((event) => event.id === id)) {
          return current;
        }
        const next = markDirty(current, "events", id);
        return {
          ...next,
          events: current.events.map((event) =>
            event.id === id ? { ...event, ...sanitizeCalendarEventPatch(patch, event) } : event,
          ),
        };
      });
    },
    addRecurring(rule: Omit<RecurringRule, "id">) {
      const id = makeId("rec");
      const sanitizedRule = sanitizeNewRecurringRule(rule);
      mutate((current) => {
        const next = markDirty(current, "recurring", id);
        return { ...next, recurring: [{ ...sanitizedRule, id }, ...current.recurring] };
      });
    },
    updateRecurring(id: string, patch: Partial<RecurringRule>) {
      mutate((current) => {
        const existing = current.recurring.find((rule) => rule.id === id);
        if (!existing) {
          return current;
        }
        const next = markDirty(current, "recurring", id);
        return {
          ...next,
          recurring: current.recurring.map((rule) =>
            rule.id === id ? { ...rule, ...sanitizeRecurringRulePatch(patch, rule) } : rule,
          ),
        };
      });
    },
    removeRecurring(id: string) {
      mutate((current) => removeCollectionItem(current, "recurring", id));
    },
    addAccount(account: Omit<Account, "id">) {
      const id = makeId("account");
      const sanitizedAccount = sanitizeNewAccount(account);
      mutate((current) => {
        const next = markDirty(current, "accounts", id);
        return { ...next, accounts: [{ ...sanitizedAccount, id }, ...current.accounts] };
      });
    },
    updateAccount(id: string, patch: Partial<Account>) {
      mutate((current) => {
        const existing = current.accounts.find((account) => account.id === id);
        if (!existing) {
          return current;
        }
        const next = markDirty(current, "accounts", id);
        return {
          ...next,
          accounts: current.accounts.map((account) =>
            account.id === id ? { ...account, ...sanitizeAccountPatch(patch, account) } : account,
          ),
        };
      });
    },
    saveDailyPlan(plan: DailyPlan) {
      const sanitized = sanitizeDailyPlan(plan);
      mutate((current) => {
        const next = markDirty(current, "dailyPlans", sanitized.id);
        const exists = current.dailyPlans.some((item) => item.id === sanitized.id);
        const dailyPlans = exists
          ? current.dailyPlans.map((item) =>
              item.id === sanitized.id ? { ...sanitized, createdAt: item.createdAt } : item,
            )
          : [sanitized, ...current.dailyPlans];
        return { ...next, dailyPlans };
      });
    },
    saveDailyReview(review: DailyReview) {
      const sanitized = sanitizeDailyReview(review);
      mutate((current) => {
        const next = markDirty(current, "dailyReviews", sanitized.id);
        const exists = current.dailyReviews.some((item) => item.id === sanitized.id);
        const dailyReviews = exists
          ? current.dailyReviews.map((item) =>
              item.id === sanitized.id ? { ...sanitized, createdAt: item.createdAt } : item,
            )
          : [sanitized, ...current.dailyReviews];
        return { ...next, dailyReviews };
      });
    },
    saveWeeklyReview(review: WeeklyReview) {
      const sanitized = sanitizeWeeklyReview(review);
      mutate((current) => {
        const next = markDirty(current, "weeklyReviews", sanitized.id);
        const exists = current.weeklyReviews.some((item) => item.id === sanitized.id);
        const weeklyReviews = exists
          ? current.weeklyReviews.map((item) =>
              item.id === sanitized.id ? { ...sanitized, createdAt: item.createdAt } : item,
            )
          : [sanitized, ...current.weeklyReviews];
        return { ...next, weeklyReviews };
      });
    },
    generateRecurringTasks() {
      // D27/D31: lazy sweep — materialize due recurring instances + prune stale future ones.
      // Returns the SAME state reference when nothing changes, so it never triggers a
      // spurious re-render / sync push when called on load/focus.
      setState((current) => {
        const { toAdd, toRemoveIds } = sweepRecurringTasks(current.tasks);
        if (toAdd.length === 0 && toRemoveIds.length === 0) {
          return current;
        }
        const removeSet = new Set(toRemoveIds);
        let next = current;
        toAdd.forEach((task) => {
          next = markDirty(next, "tasks", task.id);
        });
        toRemoveIds.forEach((id) => {
          next = removeCollectionItem(next, "tasks", id);
        });
        const tasks = [...toAdd, ...next.tasks.filter((task) => !removeSet.has(task.id))];
        return stamp({ ...next, tasks });
      });
    },
    saveProjectTemplateFromProject(projectId: string, name: string) {
      // D36: capture current non-done tasks of a project as a reusable template.
      const id = makeId("template");
      mutate((current) => {
        const project = current.projects.find((p) => p.id === projectId);
        if (!project) return current;
        const today = todayIso();
        const templateTasks: ProjectTemplate["tasks"] = current.tasks
          .filter((t) => t.projectId === projectId && t.status !== "done")
          .map((t) => ({
            title: t.title,
            priority: t.priority,
            estimateMinutes: t.estimateMinutes,
            offsetDays: captureOffsetDays(t.dueDate, today),
          }));
        const template: ProjectTemplate = {
          id,
          name: name.trim() || project.name,
          area: project.area,
          tasks: templateTasks,
          createdAt: new Date().toISOString(),
        };
        const next = markDirty(current, "templates", id);
        return { ...next, templates: [template, ...current.templates] };
      });
    },
    instantiateProjectTemplate(templateId: string, projectName: string, startDateIso: string) {
      // D36: create a new Project + Tasks from a saved template.
      mutate((current) => {
        const template = current.templates.find((t) => t.id === templateId);
        if (!template) return current;
        const projectId = makeId("project");
        const taskIds = template.tasks.map(() => makeId("task"));
        const { project, tasks } = buildProjectFromTemplate(
          template,
          projectId,
          taskIds,
          projectName,
          startDateIso,
        );
        // project already has an id; sanitize the fields by extracting id separately
        const { id: _pid, ...projectWithoutId } = project;
        const sanitizedProject = { ...sanitizeNewProject(projectWithoutId), id: _pid };
        let next = markDirty(current, "projects", projectId);
        next = { ...next, projects: [sanitizedProject, ...next.projects] };
        for (const task of tasks) {
          const { id: _tid, ...taskWithoutId } = task;
          const sanitized = { ...sanitizeNewTask(taskWithoutId), id: _tid };
          next = markDirty(next, "tasks", _tid);
          next = { ...next, tasks: [sanitized, ...next.tasks] };
        }
        return next;
      });
    },
    removeProjectTemplate(id: string) {
      mutate((current) => removeCollectionItem(current, "templates", id));
    },
    setCurrencyRate(code: string, rateToVnd: number) {
      // D38: validate — must be a 3-letter ISO code and rateToVnd > 0
      const normalized = code.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(normalized) || rateToVnd <= 0) return;
      mutate((current) => {
        const existing = current.currencyRates.find((r) => r.id === normalized);
        const now = new Date().toISOString();
        const rate: CurrencyRate = existing
          ? { ...existing, rateToVnd, updatedAt: now }
          : { id: normalized, rateToVnd, updatedAt: now, createdAt: now };
        const next = markDirty(current, "currencyRates", normalized);
        return {
          ...next,
          currencyRates: existing
            ? current.currencyRates.map((r) => (r.id === normalized ? rate : r))
            : [rate, ...current.currencyRates],
        };
      });
    },
    removeCurrencyRate(code: string) {
      const normalized = code.trim().toUpperCase();
      mutate((current) => removeCollectionItem(current, "currencyRates", normalized));
    },
    // D43: managed finance categories — id deterministic theo slug tên.
    addFinanceCategory(name: string, kind: TransactionType, style?: { icon?: string; color?: string }) {
      const trimmed = name.trim();
      if (!trimmed) return;
      const id = financeCategoryId(trimmed);
      mutate((current) => {
        const existing = current.financeCategories.find((c) => c.id === id);
        const next = markDirty(current, "financeCategories", id);
        if (existing) {
          // Trùng tên (kể cả đang ẩn) → bỏ ẩn thay vì nhân đôi.
          return {
            ...next,
            financeCategories: current.financeCategories.map((c) =>
              c.id === id ? { ...c, name: trimmed, kind, archived: undefined } : c,
            ),
          };
        }
        // D44: màu mặc định xoay vòng theo bảng màu, icon mặc định "tag".
        const cat: FinanceCategory = {
          id,
          name: trimmed,
          kind,
          icon: style?.icon ?? "tag",
          color: style?.color ?? CATEGORY_COLOR_PALETTE[current.financeCategories.length % CATEGORY_COLOR_PALETTE.length],
          createdAt: new Date().toISOString(),
        };
        return { ...next, financeCategories: [cat, ...current.financeCategories] };
      });
    },
    // D44: đổi icon/màu danh mục (Finance v2 visual).
    setFinanceCategoryStyle(id: string, style: { icon?: string; color?: string }) {
      mutate((current) => {
        if (!current.financeCategories.some((c) => c.id === id)) return current;
        const next = markDirty(current, "financeCategories", id);
        return {
          ...next,
          financeCategories: current.financeCategories.map((c) =>
            c.id === id ? { ...c, ...("icon" in style ? { icon: style.icon } : {}), ...("color" in style ? { color: style.color } : {}) } : c,
          ),
        };
      });
    },
    // Đổi tên danh mục + propagate sang transactions/budgets/recurring đang dùng tên cũ
    // (Transaction.category lưu TÊN — không đổi theo thì lịch sử tách thành 2 nhóm).
    renameFinanceCategory(id: string, newName: string) {
      const trimmed = newName.trim();
      if (!trimmed) return;
      mutate((current) => {
        const target = current.financeCategories.find((c) => c.id === id);
        if (!target || target.name === trimmed) return current;
        const oldName = target.name;
        let next = markDirty(current, "financeCategories", id);
        next = {
          ...next,
          financeCategories: next.financeCategories.map((c) => (c.id === id ? { ...c, name: trimmed } : c)),
        };
        for (const t of next.transactions) {
          if (t.category === oldName) next = markDirty(next, "transactions", t.id);
        }
        for (const b of next.budgets) {
          if (b.category === oldName) next = markDirty(next, "budgets", b.id);
        }
        for (const r of next.recurring) {
          if (r.category === oldName) next = markDirty(next, "recurring", r.id);
        }
        return {
          ...next,
          transactions: next.transactions.map((t) => (t.category === oldName ? { ...t, category: trimmed } : t)),
          budgets: next.budgets.map((b) => (b.category === oldName ? { ...b, category: trimmed } : b)),
          recurring: next.recurring.map((r) => (r.category === oldName ? { ...r, category: trimmed } : r)),
        };
      });
    },
    setFinanceCategoryArchived(id: string, archived: boolean) {
      mutate((current) => {
        if (!current.financeCategories.some((c) => c.id === id)) return current;
        const next = markDirty(current, "financeCategories", id);
        return {
          ...next,
          financeCategories: current.financeCategories.map((c) =>
            c.id === id ? { ...c, archived: archived ? true : undefined } : c,
          ),
        };
      });
    },
    // Seed bộ danh mục chuẩn + hấp thụ danh mục cũ — gọi 1 lần mỗi lần mount.
    // markDirty từng item để seed được đẩy lên cloud (merge giữ local dirty items).
    // Danh sách đã có item → BACKFILL icon/màu cho dữ liệu tạo trước D44.
    seedDefaultFinanceCategories() {
      mutate((current) => {
        if (current.financeCategories.length === 0) {
          const seeded = buildSeedFinanceCategories(current);
          let next = current;
          for (const cat of seeded) {
            next = markDirty(next, "financeCategories", cat.id);
          }
          return { ...next, financeCategories: seeded };
        }
        // Backfill: danh mục chuẩn nhận icon/màu chuẩn (khớp theo id slug),
        // danh mục khác nhận tag + màu xoay vòng.
        const standardById = new Map(
          STANDARD_FINANCE_CATEGORIES.map((c) => [financeCategoryId(c.name), c]),
        );
        let next = current;
        let changed = false;
        const updated = current.financeCategories.map((c, i) => {
          if (c.icon && c.color) return c;
          const std = standardById.get(c.id);
          changed = true;
          next = markDirty(next, "financeCategories", c.id);
          return {
            ...c,
            icon: c.icon ?? std?.icon ?? "tag",
            color: c.color ?? std?.color ?? CATEGORY_COLOR_PALETTE[i % CATEGORY_COLOR_PALETTE.length],
          };
        });
        return changed ? { ...next, financeCategories: updated } : current;
      });
    },
    remove(collection: LifeOsCollectionName, id: string) {
      mutate((current) => {
        // Gather Storage paths for cloud-backed items before removing from state.
        if (collection === "notes") {
          const note = current.notes.find((n) => n.id === id);
          const paths = (note?.attachments ?? []).map((a) => a.path).filter(Boolean) as string[];
          if (paths.length > 0) void deleteAttachmentObjects(paths);
        }
        if (collection === "transactions") {
          const txn = current.transactions.find((t) => t.id === id);
          const path = txn?.receipt?.path;
          if (path) void deleteAttachmentObjects([path]);
        }

        let next = current;
        if (collection === "tasks") {
          // Deleting a generated recurring instance must also record an exdate on its
          // parent, otherwise the deterministic-id sweep resurrects it on next load.
          const instance = current.tasks.find((task) => task.id === id);
          const parent = instance?.recurrenceParentId
            ? current.tasks.find((task) => task.id === instance.recurrenceParentId)
            : undefined;
          // Exclude the ORIGINAL occurrence date (from recurrenceKey), not dueDate —
          // a rescheduled instance would otherwise regenerate on its original date.
          const occurrenceDate =
            instance && parent && instance.recurrenceKey?.startsWith(`${parent.id}:`)
              ? instance.recurrenceKey.slice(parent.id.length + 1)
              : instance?.dueDate;
          if (instance && parent && occurrenceDate && !(parent.recurrenceExdates ?? []).includes(occurrenceDate)) {
            next = {
              ...markDirty(next, "tasks", parent.id),
              tasks: next.tasks.map((task) =>
                task.id === parent.id
                  ? { ...task, recurrenceExdates: [...(parent.recurrenceExdates ?? []), occurrenceDate] }
                  : task,
              ),
            };
          }
        }
        return removeCollectionItem(next, collection, id);
      });
    },
    replaceState(nextState: LifeOsState) {
      setState((current) => prepareReplacementState(current, nextState));
    },
    // D50: dọn tham chiếu ví mồ côi → gán về ví đầu tiên (hoặc ví mặc định ngầm).
    reassignOrphanedFinanceRefs() {
      mutate((current) => {
        const { transactionIds, recurringIds } = findOrphanedFinanceRefs(current);
        if (!transactionIds.length && !recurringIds.length) return current;
        const target = current.accounts[0]?.id ?? DEFAULT_ACCOUNT_ID;
        const valid = new Set<string>([DEFAULT_ACCOUNT_ID, ...current.accounts.map((a) => a.id)]);
        const txnFix = new Set(transactionIds);
        const recFix = new Set(recurringIds);
        let next: LifeOsState = {
          ...current,
          transactions: current.transactions.map((t) => {
            if (!txnFix.has(t.id)) return t;
            const patched = { ...t };
            if (t.accountId && !valid.has(t.accountId)) patched.accountId = target;
            if (t.toAccountId && !valid.has(t.toAccountId)) patched.toAccountId = target;
            return patched;
          }),
          recurring: current.recurring.map((r) =>
            recFix.has(r.id) ? { ...r, accountId: target } : r,
          ),
        };
        for (const id of transactionIds) next = markDirty(next, "transactions", id);
        for (const id of recurringIds) next = markDirty(next, "recurring", id);
        return next;
      });
    },
  };
}

export type LifeOsActions = ReturnType<typeof buildLifeOsActions>;
